import React, { useMemo, useState } from 'react';
import { usePos } from '../context/PosContext';
import { formatDate, formatDateTime } from '../services/datetime';
import { tenderTotals } from '../services/payments';
import { printReportAuto } from '../services/receipt';
import './Sections.css';
import './Reports.css';

interface Card {
  key: string;
  title: string;
  desc: string;
}

// Sections + cards mirror report_layout.xml.
const SECTIONS: { label: string; cards: Card[] }[] = [
  {
    label: 'Daily Update :',
    cards: [
      { key: 'today_summary', title: "Today's Summary", desc: 'A detailed breakdown of daily transactions.' },
      { key: 'bill_sales', title: 'Bill Sales', desc: 'Analyzing transactions and revenue from bills.' },
      { key: 'product_sales', title: 'Product Sales', desc: 'Tracking product sales and performance.' },
      { key: 'customer_report', title: 'Customer Report', desc: 'Understanding customer behavior and interactions.' },
      { key: 'gst_sales', title: 'GST Sales', desc: 'Tax-wise sales breakdown for the period.' },
      { key: 'payment_mode', title: 'Payment Mode Report', desc: 'Sales split across cash, card and digital.' },
      { key: 'salesman_sales', title: 'Salesman Sales', desc: 'Bills, total and average per salesman.' },
    ],
  },
  {
    label: 'SHIFT Details :',
    cards: [
      { key: 'shift_summary', title: 'Shift Sales Summary', desc: 'Reviewing sales during different shifts.' },
      { key: 'invoice_detail', title: 'Invoice Detail', desc: 'Analyzing transactions and revenue from bills.' },
    ],
  },
  {
    label: 'Product Details :',
    cards: [
      { key: 'stock_summary', title: 'Stock Summary', desc: 'An overview of inventory and stock.' },
      { key: 'goods_received', title: 'Goods Received', desc: 'Inventory received into stock.' },
      { key: 'stock_out', title: 'Stock Out', desc: 'Tracking instances of inventory depletion.' },
      { key: 'cashflow_report', title: 'CashFlow Report', desc: 'Advance, credit, expense and received entries.' },
      { key: 'day_sales', title: 'Day Sales', desc: 'Tracking revenue and trends for each day.' },
      { key: 'category_sales', title: 'Category Sales', desc: 'Examining product category performance.' },
      { key: 'product_list', title: 'Product List', desc: 'A comprehensive list of available products.' },
      { key: 'tax_report', title: 'Tax Report', desc: 'Summarizing tax-related data for reporting.' },
      { key: 'stock_return', title: 'Stock Return', desc: 'Inventory returned back into stock.' },
      { key: 'delete_bill', title: 'Delete Bill', desc: 'Bills that were voided or cancelled.' },
    ],
  },
  {
    label: 'Print Report :',
    cards: [
      { key: 'stock_report', title: 'Stock Report', desc: 'Inventory status: stock levels and stock value.' },
      { key: 'out_of_stock', title: 'Out Of Stock', desc: 'Products with no remaining inventory.' },
      { key: 'reorder_level', title: 'Reorder Level', desc: 'Products at or below their reorder level.' },
      { key: 'expiry_report', title: 'Batch & Expiry', desc: 'Batch numbers and expiry status of products.' },
    ],
  },
];

interface Result {
  title: string;
  columns: string[];
  rows: (string | number)[][];
  numericCols?: number[];
}

const Reports: React.FC = () => {
  const { sales, customers, products, cashflow, movements, formatMoney, config } = usePos();
  const today = new Date().toISOString().split('T')[0];
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [result, setResult] = useState<Result | null>(null);

  const inRange = (iso: string) => {
    const t = new Date(iso).getTime();
    return t >= new Date(start).getTime() && t <= new Date(end + 'T23:59:59').getTime();
  };

  const completed = useMemo(() => sales.filter((s) => s.status === 'completed' && inRange(s.timestamp)), [sales, start, end]); // eslint-disable-line

  const run = (key: string): Result => {
    const m = (n: number) => formatMoney(n);
    switch (key) {
      case 'today_summary':
      case 'shift_summary': {
        const t = tenderTotals(completed);
        return {
          title: key === 'shift_summary' ? 'Shift Sales Summary' : "Today's Summary",
          columns: ['Metric', 'Value'],
          rows: [
            ['Bills', completed.length],
            ['Cash Sales', m(t.cash)],
            ['Card Sales', m(t.card)],
            ['Digital Sales', m(t.digital)],
            ['Credit Sales', m(t.credit)],
            ['Discount', m(completed.reduce((a, s) => a + s.discount, 0))],
            ['Tax', m(completed.reduce((a, s) => a + s.tax, 0))],
            ['Total Sales', m(completed.reduce((a, s) => a + s.total, 0))],
          ],
        };
      }
      case 'bill_sales':
      case 'invoice_detail':
        return {
          title: key === 'invoice_detail' ? 'Invoice Detail' : 'Bill Sales',
          columns: ['Bill No', 'Date', 'Customer', 'Items', 'Total'],
          rows: completed.map((s) => [s.billNo, formatDateTime(s.timestamp), s.customerName ?? 'Walk-in', s.items.length, m(s.total)]),
          numericCols: [4],
        };
      case 'product_sales': {
        const map = new Map<string, { name: string; qty: number; rev: number }>();
        completed.forEach((s) => s.items.forEach((i) => {
          const r = map.get(i.productId) ?? { name: i.name, qty: 0, rev: 0 };
          r.qty += i.quantity; r.rev += i.total; map.set(i.productId, r);
        }));
        return {
          title: 'Product Sales',
          columns: ['Product', 'Qty', 'Revenue'],
          rows: Array.from(map.values()).sort((a, b) => b.rev - a.rev).map((r) => [r.name, r.qty, m(r.rev)]),
          numericCols: [2],
        };
      }
      case 'customer_report': {
        const map = new Map<string, { bills: number; total: number }>();
        completed.forEach((s) => {
          const k = s.customerName ?? 'Walk-in';
          const r = map.get(k) ?? { bills: 0, total: 0 };
          r.bills += 1; r.total += s.total; map.set(k, r);
        });
        return {
          title: 'Customer Report',
          columns: ['Customer', 'Bills', 'Total'],
          rows: Array.from(map.entries()).map(([k, v]) => [k, v.bills, m(v.total)]),
          numericCols: [2],
        };
      }
      case 'gst_sales':
      case 'tax_report': {
        const map = new Map<number, { taxable: number; tax: number }>();
        completed.forEach((s) => {
          const share = s.subtotal > 0 ? s.tax / s.subtotal : 0;
          s.items.forEach((i) => {
            const rate = i.taxRatePercent;
            const r = map.get(rate) ?? { taxable: 0, tax: 0 };
            r.taxable += i.total; r.tax += i.total * share; map.set(rate, r);
          });
        });
        return {
          title: key === 'tax_report' ? 'Tax Report' : 'GST Sales',
          columns: ['GST %', 'Taxable', 'Tax', 'CGST', 'SGST'],
          rows: Array.from(map.entries()).sort((a, b) => a[0] - b[0]).map(([rate, v]) => [`${rate}%`, m(v.taxable), m(v.tax), m(v.tax / 2), m(v.tax / 2)]),
        };
      }
      case 'payment_mode': {
        // Attribute split bills per tender (amount per line; a split bill counts
        // toward each tender it used).
        const map = new Map<string, { count: number; amount: number }>();
        const add = (mode: string, amount: number) => {
          const r = map.get(mode) ?? { count: 0, amount: 0 };
          r.count += 1; r.amount += amount; map.set(mode, r);
        };
        completed.forEach((s) => {
          if (s.payments && s.payments.length > 1) s.payments.forEach((p) => add(p.method, p.amount));
          else add(s.paymentMethod, s.total);
        });
        return {
          title: 'Payment Mode Report',
          columns: ['Mode', 'Bills', 'Amount'],
          rows: Array.from(map.entries()).map(([k, v]) => [k.toUpperCase(), v.count, m(v.amount)]),
          numericCols: [2],
        };
      }
      case 'salesman_sales': {
        const map = new Map<string, { bills: number; total: number }>();
        completed.forEach((s) => {
          const key = s.salesman || 'Unassigned';
          const r = map.get(key) ?? { bills: 0, total: 0 };
          r.bills += 1; r.total += s.total; map.set(key, r);
        });
        return {
          title: 'Salesman Sales',
          columns: ['Salesman', 'Bills', 'Total', 'Average'],
          rows: Array.from(map.entries())
            .sort((a, b) => b[1].total - a[1].total)
            .map(([k, v]) => [k, v.bills, m(v.total), m(v.total / v.bills)]),
          numericCols: [1, 2, 3],
        };
      }
      case 'day_sales': {
        const map = new Map<string, { bills: number; total: number }>();
        completed.forEach((s) => {
          const k = formatDate(s.timestamp);
          const r = map.get(k) ?? { bills: 0, total: 0 };
          r.bills += 1; r.total += s.total; map.set(k, r);
        });
        return {
          title: 'Day Sales',
          columns: ['Date', 'Bills', 'Total'],
          rows: Array.from(map.entries()).map(([k, v]) => [k, v.bills, m(v.total)]),
          numericCols: [2],
        };
      }
      case 'category_sales': {
        const cat = (pid: string) => products.find((p) => p.id === pid)?.category || 'Uncategorized';
        const map = new Map<string, { qty: number; rev: number }>();
        completed.forEach((s) => s.items.forEach((i) => {
          const k = cat(i.productId);
          const r = map.get(k) ?? { qty: 0, rev: 0 };
          r.qty += i.quantity; r.rev += i.total; map.set(k, r);
        }));
        return {
          title: 'Category Sales',
          columns: ['Category', 'Qty', 'Revenue'],
          rows: Array.from(map.entries()).map(([k, v]) => [k, v.qty, m(v.rev)]),
          numericCols: [2],
        };
      }
      case 'product_list':
        return {
          title: 'Product List',
          columns: ['Name', 'Category', 'Price', 'Stock'],
          rows: products.map((p) => [p.name, p.category ?? '—', m(p.price), p.stock]),
        };
      case 'stock_summary':
        return {
          title: 'Stock Summary',
          columns: ['Product', 'Stock', 'Reorder', 'Status'],
          rows: products.map((p) => [p.name, p.stock, p.reorderLevel ?? '—', p.stock <= (p.reorderLevel ?? 10) ? 'LOW' : 'OK']),
        };
      case 'goods_received':
        return {
          title: 'Goods Received',
          columns: ['Date', 'Product', 'Qty', 'Note'],
          rows: movements.filter((x) => x.reason === 'goods_received' && inRange(x.timestamp)).reverse().map((x) => [formatDateTime(x.timestamp), x.productName, x.quantity, x.note ?? '—']),
        };
      case 'stock_out':
        return {
          title: 'Stock Out',
          columns: ['Date', 'Product', 'Qty', 'Reason'],
          rows: movements.filter((x) => x.reason === 'stock_out' && inRange(x.timestamp)).reverse().map((x) => [formatDateTime(x.timestamp), x.productName, x.quantity, x.note ?? '—']),
        };
      case 'cashflow_report':
        return {
          title: 'CashFlow Report',
          columns: ['Date', 'Type', 'Party', 'Note', 'Amount'],
          rows: cashflow.filter((c) => inRange(c.timestamp)).reverse().map((c) => [formatDateTime(c.timestamp), c.type, c.customerName ?? '—', c.note ?? '—', m(c.amount)]),
        };
      case 'stock_return':
        return {
          title: 'Stock Return',
          columns: ['Date', 'Product', 'Qty', 'Note'],
          rows: movements.filter((x) => x.reason === 'sale_return' && inRange(x.timestamp)).reverse().map((x) => [formatDateTime(x.timestamp), x.productName, x.quantity, x.note ?? '—']),
          numericCols: [2],
        };
      case 'delete_bill': {
        const voided = sales.filter((s) => s.status === 'cancelled' && inRange(s.timestamp));
        return {
          title: 'Delete Bill',
          columns: ['Bill No', 'Date', 'Customer', 'Items', 'Total'],
          rows: voided.map((s) => [s.billNo, formatDateTime(s.timestamp), s.customerName ?? 'Walk-in', s.items.length, m(s.total)]),
          numericCols: [3],
        };
      }
      case 'stock_report':
        return {
          title: 'Stock Report',
          columns: ['Product', 'Category', 'Stock', 'Unit Cost', 'Stock Value'],
          rows: products.map((p) => {
            const cost = p.costPrice ?? p.price;
            return [p.name, p.category ?? '—', p.stock, m(cost), m(cost * p.stock)];
          }),
        };
      case 'out_of_stock':
        return {
          title: 'Out Of Stock',
          columns: ['Product', 'Category', 'Stock', 'Reorder'],
          rows: products.filter((p) => p.stock <= 0).map((p) => [p.name, p.category ?? '—', p.stock, p.reorderLevel ?? '—']),
          numericCols: [2],
        };
      case 'reorder_level':
        return {
          title: 'Reorder Level',
          columns: ['Product', 'Stock', 'Reorder', 'Shortfall'],
          rows: products
            .filter((p) => p.stock <= (p.reorderLevel ?? 0) && (p.reorderLevel ?? 0) > 0)
            .map((p) => [p.name, p.stock, p.reorderLevel ?? 0, Math.max(0, (p.reorderLevel ?? 0) - p.stock)]),
          numericCols: [3],
        };
      case 'expiry_report': {
        const todayMs = Date.now();
        const soonMs = todayMs + 30 * 86400000; // within 30 days
        return {
          title: 'Batch & Expiry',
          columns: ['Product', 'Batch', 'Mfg', 'Expiry', 'Status'],
          rows: products
            .filter((p) => p.expDate)
            .map((p) => {
              const exp = new Date(p.expDate!).getTime();
              const status = exp < todayMs ? 'EXPIRED' : exp < soonMs ? 'Expiring soon' : 'OK';
              return [p.name, p.batchNo ?? '—', p.mfgDate ?? '—', p.expDate!, status];
            })
            .sort((a, b) => String(a[3]).localeCompare(String(b[3]))),
        };
      }
      default:
        return { title: 'Report', columns: ['Info'], rows: [['Not available in this build.']] };
    }
  };

  const open = (key: string) => setResult(run(key));

  const exportCsv = () => {
    if (!result) return;
    const csv = [result.columns, ...result.rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${result.title.replace(/\s+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Print the open report to a configured thermal printer (USB / Bluetooth /
  // Ethernet, ESC/POS). Falls back to the browser print dialog when none is set
  // up or the device can't be reached.
  const printUsb = () => {
    if (!result) return;
    printReportAuto(result.title, `${config.storeName} · ${start} -> ${end}`, result.columns, result.rows);
  };

  return (
    <div className="section-page rp-page">
      <div className="page-header">
        <h1>REPORT</h1>
        <div className="rp-range">
          <label>
            Start <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </label>
          <label>
            End <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </label>
        </div>
      </div>

      {SECTIONS.map((sec) => (
        <div className="rp-section" key={sec.label}>
          <div className="rp-section-label">{sec.label}</div>
          <div className="rp-cards">
            {sec.cards.map((c) => (
              <button key={c.key} className="rp-card" onClick={() => open(c.key)}>
                <span className="rp-card-title">{c.title}</span>
                <span className="rp-card-desc">{c.desc}</span>
              </button>
            ))}
          </div>
        </div>
      ))}

      {result && (
        <div className="modal-overlay" onClick={() => setResult(null)}>
          <div className="rp-result" onClick={(e) => e.stopPropagation()}>
            <div className="rp-result-head">
              <h2>{result.title}</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-grey" onClick={() => window.print()}>Print</button>
                <button className="btn-grey" onClick={printUsb}>🖨 USB Print</button>
                <button className="btn-grey" onClick={exportCsv}>CSV</button>
                <button className="btn-teal" onClick={() => setResult(null)}>Close</button>
              </div>
            </div>
            <p className="muted" style={{ marginBottom: 8 }}>{start} → {end}</p>
            <div className="rp-result-scroll">
              <table className="section-table">
                <thead>
                  <tr>
                    {result.columns.map((c, i) => (
                      <th key={i} className={i === result.columns.length - 1 ? 'right' : ''}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.length === 0 ? (
                    <tr><td colSpan={result.columns.length} className="muted">No data in range.</td></tr>
                  ) : (
                    result.rows.map((row, ri) => (
                      <tr key={ri}>
                        {row.map((cell, ci) => (
                          <td key={ci} className={ci === row.length - 1 ? 'right' : ''}>{cell}</td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
